import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon, ExternalLinkIcon, MailIcon, PresentationIcon, TargetIcon, UsersIcon } from 'lucide-react';
import { InteriorSlide, SlideBanner } from './SlideShell';
import type { SlideProps } from './SlideShell';
import { PdfImage } from '../PdfImage';
import { NordicCaseOverlay } from '../nordicCase/NordicCaseOverlay';
import { BOEHRINGER_SNAK_URL, boehringerImages } from '../../data/boehringerSnak';
const BRIEF = [{
  label: 'The problem',
  icon: AlertTriangleIcon,
  value: '127,031 Danes live with both T2D and CVD — yet 47% get neither SGLT2 nor GLP-1. That is ~60,000 high-risk patients without organ protection.'
}, {
  label: 'The goal',
  icon: TargetIcon,
  value: 'Raise awareness of the gap and help practices find these patients via their care-pathway plans (KiAP).'
}, {
  label: 'Audience',
  icon: UsersIcon,
  value: 'GPs and practice nurses across Denmark.'
}, {
  label: 'Channel',
  icon: MailIcon,
  value: 'Email with a Snakker .gif to ~2,200 HCPs (975 target, 1,188 non-target), leading to KiAP material and a 12 June webinar.'
}] as const;
const STEPS = [{
  job: 'Email invite',
  title: 'The hook in the inbox',
  line: '~2,200 GPs and nurses invited with a Snakker-designed .gif.',
  crop: boehringerImages.emailThumb
}, {
  job: 'Opening insight',
  title: '332,507 Danes with T2D',
  line: 'One striking number earns the next tap.',
  crop: boehringerImages.statScreen
}, {
  job: 'Knowledge quiz',
  title: 'SGLT2 or GLP-1?',
  line: '57 viewers answered — almost half got it wrong.',
  crop: boehringerImages.quizScreen
}, {
  job: 'Instant feedback',
  title: 'Correct!',
  line: '47% of high-risk patients lack organ-protective treatment.',
  crop: boehringerImages.correctScreen
}, {
  job: 'Reflection',
  title: 'In doubt?',
  line: 'A pause that asks HCPs to check their own patient list.',
  crop: boehringerImages.doubtScreen
}, {
  job: 'Clear next step',
  title: 'Want to know more?',
  line: '83% chose the care-pathway (KiAP) material — 10 click-throughs.',
  crop: boehringerImages.ctaScreen
}] as const;
export function SlideAlreadyAtBoehringer({}: SlideProps) {
  const [caseOpen, setCaseOpen] = useState(false);
  return <InteriorSlide surface banner={<SlideBanner eyebrow="T2D patients with CVD · Denmark" data-id="element-201" data-mp="1qef773uopz7o">
          One Snak turned an awareness message into a measurable journey: 227
          page views, 116 plays and 3m 9s average view time.
        </SlideBanner>} data-id="element-200" data-mp="1qdm6okmbk5xz">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="flex flex-shrink-0 items-end justify-between gap-8" data-id="element-202" data-mp="1vdi9whn6ztch">
        <div className="min-w-0" data-id="element-203" data-mp="12pcodsobe0bl">
          <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-204" data-mp="12ob1mqxxrpe1">
            Already happening at Boehringer Ingelheim
          </p>
          <h2 className="text-[clamp(1.9rem,3.6vw,3.3rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-205" data-mp="3j47nuna4rmgc">
            Boehringer is already{' '}
            <span className="text-[var(--brand)]" data-id="element-206" data-mp="39cblv14xbc6u">Snakifying.</span>
          </h2>
        </div>
        <div className="hidden flex-shrink-0 flex-col items-end gap-2 lg:flex" data-id="element-207" data-mp="wiaf0pg8qwn">
          <p className="max-w-xs text-right text-[clamp(0.78rem,1.05vw,0.98rem)] font-semibold leading-snug text-[var(--muted)]" data-id="element-208" data-mp="5w4iywuc1iz4">
            31 March – 6 May 2025. One story, six steps.
          </p>
          <div className="flex items-center gap-2" data-id="element-209" data-mp="3t0swrjp9thpg">
          <button type="button" onClick={() => setCaseOpen(true)} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--fg)] px-3.5 py-1.5 text-[clamp(0.7rem,0.9vw,0.85rem)] font-bold text-white transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2" data-id="element-210" data-mp="3sz9cli0humie">
            <PresentationIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-211" data-mp="2s3snt9xhxt4r" />
            Nordic case &amp; outcome
          </button>
          <a href={BOEHRINGER_SNAK_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-[var(--brand)] px-3.5 py-1.5 text-[clamp(0.7rem,0.9vw,0.85rem)] font-bold text-white transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2" data-id="element-212" data-mp="2n34vmkxj768y">
            View the live Snak
            <ExternalLinkIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-213" data-mp="31tc7uiwy3t17" />
          </a>
          </div>
        </div>
      </motion.header>

      <div className="mt-[clamp(0.9rem,1.8vw,1.6rem)] flex min-h-0 flex-1 gap-[clamp(0.6rem,1.2vw,1.1rem)]" data-id="element-214" data-mp="1vbyqtr6v8kdc">
      <motion.aside initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.12,
        duration: 0.5
      }} aria-label="Campaign brief" className="flex w-[32%] min-w-0 flex-shrink-0 flex-col rounded-2xl bg-[var(--fg)] p-[clamp(0.9rem,1.5vw,1.4rem)] text-white" data-id="element-215" data-mp="1qhl04235f1f0">
        <p className="text-[clamp(0.55rem,0.78vw,0.7rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand-lighter)]" data-id="element-216" data-mp="joui5avl60ki">
          The brief
        </p>
        <dl className="mt-[clamp(0.5rem,0.9vw,0.8rem)] flex min-h-0 flex-1 flex-col justify-between gap-[clamp(0.4rem,0.8vw,0.7rem)]" data-id="element-217" data-mp="ops1vrw8v2o4">
          {BRIEF.map(item => <div key={item.label} className="flex min-w-0 gap-[clamp(0.5rem,0.8vw,0.75rem)]" data-id="element-218" data-mp="ymd4k6ksk1os">
              <span className="flex h-[clamp(1.6rem,2vw,2rem)] w-[clamp(1.6rem,2vw,2rem)] flex-shrink-0 items-center justify-center rounded-lg bg-white/10" data-id="element-219" data-mp="12m5iyzwv9222">
                <item.icon className="h-[clamp(0.8rem,1vw,1rem)] w-[clamp(0.8rem,1vw,1rem)] text-[var(--brand-lighter)]" aria-hidden="true" data-id="element-220" data-mp="34ejunh8thaa1" />
              </span>
              <div className="min-w-0" data-id="element-221" data-mp="6m1oy26dbc63">
                <dt className="text-[clamp(0.55rem,0.75vw,0.68rem)] font-bold uppercase tracking-[0.16em] text-white/60" data-id="element-222" data-mp="6n31kb3186px">
                  {item.label}
                </dt>
                <dd className="mt-0.5 text-[clamp(0.68rem,0.95vw,0.9rem)] font-semibold leading-snug text-white" data-id="element-223" data-mp="gid18s5yk20j">
                  {item.value}
                </dd>
              </div>
            </div>)}
        </dl>
      </motion.aside>

      <div className="grid min-h-0 flex-1 grid-cols-2 grid-rows-3 gap-[clamp(0.5rem,1vw,0.95rem)]" data-id="element-224" data-mp="pi9vraf13v3i">
        {STEPS.map((step, index) => <motion.article key={step.job} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.16 + index * 0.08,
          duration: 0.45
        }} className="flex min-h-0 overflow-hidden rounded-2xl border border-[var(--fg)]/10 bg-white shadow-sm" data-id="element-225" data-mp="z9nrr87rucrw" data-mpi={index}>
            <div className="relative flex w-[42%] flex-shrink-0 items-center justify-center overflow-hidden bg-white p-2" data-id="element-226" data-mp="1rx6xpn5iggub">
              <PdfImage crop={step.crop} alt={`${step.title} — Boehringer Snak screen`} className="max-h-full min-h-0 w-full rounded-md object-contain ring-1 ring-[var(--fg)]/10" data-id="element-227" data-mp="1ivpnglesb9dj" />
            </div>
            <div className="flex min-w-0 flex-1 flex-col justify-center p-[clamp(0.7rem,1.2vw,1.15rem)]" data-id="element-228" data-mp="194bhpvw8pdby">
              <p className="text-[clamp(0.55rem,0.78vw,0.7rem)] font-bold uppercase tracking-[0.16em] text-[var(--brand)]" data-id="element-229" data-mp="1dxxjuu4s1yti">
                {String(index + 1).padStart(2, '0')} · {step.job}
              </p>
              <h3 className="mt-1 text-[clamp(0.9rem,1.35vw,1.3rem)] font-extrabold leading-tight tracking-[-0.025em] text-[var(--fg)]" data-id="element-230" data-mp="14730bxix1dbr">
                {step.title}
              </h3>
              <p className="mt-[clamp(0.35rem,0.7vw,0.6rem)] text-[clamp(0.68rem,0.95vw,0.88rem)] leading-snug text-[var(--muted)]" data-id="element-231" data-mp="v5ll0hdc1fo4">
                {step.line}
              </p>
            </div>
          </motion.article>)}
      </div>
      </div>
      <NordicCaseOverlay open={caseOpen} onClose={() => setCaseOpen(false)} data-id="element-232" data-mp="10pfgly4rb3kn" />
    </InteriorSlide>;
}